import {
  DownloadOutlined,
  FileImageOutlined,
  FileOutlined,
  SoundOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { App, Button, Image, Space, Tag, Tooltip, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { fetchMessageMediaContent } from '@/services/api/messages'
import type { MessageMedia } from '@/types/message'

const { Text } = Typography

function mediaIcon(type: string) {
  if (type === 'image') return <FileImageOutlined />
  if (type === 'voice') return <SoundOutlined />
  if (type === 'video') return <VideoCameraOutlined />
  return <FileOutlined />
}

function mediaStatusColor(status: string) {
  if (status === 'available') return 'green'
  if (status === 'pending' || status === 'downloading') return 'blue'
  if (status === 'failed' || status === 'expired') return 'red'
  return 'default'
}

function mediaStatusText(status: string, hasContent = false) {
  if (hasContent) return '可下载'
  const labels: Record<string, string> = {
    metadata_only: '未下载',
    pending: '已排队',
    downloading: '下载中',
    available: '可下载',
    failed: '下载失败',
    expired: '已过期',
  }
  return labels[status] || status
}

function mediaTypeText(type: string) {
  const labels: Record<string, string> = {
    image: '图片',
    file: '文件',
    voice: '语音',
    video: '视频',
  }
  return labels[type] || type
}

function formatBytes(value: number | null) {
  if (!value || value <= 0) return '-'
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / 1024 / 1024).toFixed(1)} MB`
}

interface Props {
  items: MessageMedia[]
  pendingMediaUid?: string
  onQueueDownload: (mediaUid: string) => void
  title?: string | false
}

function AuthenticatedImagePreview({ item }: { item: MessageMedia }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!item.download_url) return
    let revoked = false
    let nextObjectUrl: string | null = null

    setFailed(false)
    setObjectUrl(null)
    fetchMessageMediaContent(item.download_url)
      .then((blob) => {
        if (revoked) return
        nextObjectUrl = URL.createObjectURL(blob)
        setObjectUrl(nextObjectUrl)
      })
      .catch(() => {
        if (!revoked) setFailed(true)
      })

    return () => {
      revoked = true
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl)
    }
  }, [item.download_url])

  if (failed || !objectUrl) {
    return <Text type="secondary">{mediaIcon(item.media_type)}</Text>
  }

  return (
    <Image
      src={objectUrl}
      width={64}
      height={64}
      style={{ objectFit: 'cover', borderRadius: 4 }}
      alt={item.display_name || '图片'}
    />
  )
}

export default function MessageMediaList({ items, pendingMediaUid, onQueueDownload, title = '媒体文件' }: Props) {
  const { message } = App.useApp()
  const [downloadingMediaUid, setDownloadingMediaUid] = useState<string | null>(null)

  if (items.length === 0) return null

  async function downloadMedia(item: MessageMedia) {
    if (!item.download_url) return
    setDownloadingMediaUid(item.media_uid)
    try {
      const blob = await fetchMessageMediaContent(item.download_url)
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = item.display_name || `${item.media_uid}.${item.file_ext || 'bin'}`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
    } catch {
      message.error('下载失败')
    } finally {
      setDownloadingMediaUid(null)
    }
  }

  return (
    <div>
      {title && <Text strong>{title}</Text>}
      <div style={{ display: 'grid', gap: 8, marginTop: title ? 8 : 0 }}>
        {items.map((item) => {
          const hasContent = !!item.download_url || item.content_available === true
          const canQueue = ['metadata_only', 'failed', 'expired'].includes(item.download_status)
          const busy = ['pending', 'downloading'].includes(item.download_status)
          const isImagePreview =
            item.media_type === 'image' && hasContent && !!item.download_url

          return (
            <div
              key={item.media_uid}
              style={{
                display: 'grid',
                gridTemplateColumns: isImagePreview ? '72px minmax(0, 1fr) auto' : '24px minmax(0, 1fr) auto',
                alignItems: 'center',
                gap: 12,
                padding: 12,
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                background: '#fff',
              }}
            >
              {isImagePreview ? (
                <AuthenticatedImagePreview item={item} />
              ) : (
                <Text type="secondary">{mediaIcon(item.media_type)}</Text>
              )}

              <div style={{ minWidth: 0 }}>
                <Text strong ellipsis style={{ display: 'block' }}>
                  {item.display_name || mediaTypeText(item.media_type)}
                </Text>
                <Space size={6} wrap>
                  <Tag>{mediaTypeText(item.media_type)}</Tag>
                  <Tag color={hasContent ? 'green' : mediaStatusColor(item.download_status)}>
                    {mediaStatusText(item.download_status, hasContent)}
                  </Tag>
                  <Text type="secondary">{formatBytes(item.size_bytes)}</Text>
                  {item.file_ext && <Text type="secondary">.{item.file_ext}</Text>}
                </Space>
              </div>

              <Space size={8}>
                {item.download_url ? (
                  <Tooltip title="下载">
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      loading={downloadingMediaUid === item.media_uid}
                      onClick={() => void downloadMedia(item)}
                    />
                  </Tooltip>
                ) : (
                  <Button
                    size="small"
                    disabled={!canQueue || busy}
                    loading={pendingMediaUid === item.media_uid}
                    onClick={() => onQueueDownload(item.media_uid)}
                  >
                    加入下载队列
                  </Button>
                )}
              </Space>
            </div>
          )
        })}
      </div>
    </div>
  )
}
